/* eslint-disable max-len */
/* eslint-disable camelcase */
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState
} from "react";

import { useMutation, useQuery } from "@apollo/client";
import { Box, Checkbox, Stack, Typography } from "@mui/material";

import { useToast } from "@npm-telluria-tecnologia/telluria-ui";

import ModuleHex from "@atoms/ModuleHex";
import useTranslation from "@languages/useTranslation";
import { UserRegisterMessages, GlobalMessages } from "@languages/interfaces";
import { MODULE_PRESENTATION } from "src/pages/Launcher/catalog";
import { useTenantProducts } from "@hooks/useTenantProducts";

import { TUserByIdQueries, userById } from "src/store/graphql/groups/user/query.graphql";
import {
  revokeUserModuleAccess,
  TRevokeUserModuleAccessMutations,
  TUserModuleAccessCreateMutations,
  userModuleAccessCreate
} from "src/store/graphql/groups/userModuleAccess/mutation.graphql";
import {
  ECommandResultStatus,
  UserModuleAccessMutationsCreateArgs,
  UserModuleAccessMutationsRevokeUserModuleAccessArgs,
  UserQueriesGetByIdArgs
} from "src/store/graphql/helpers/types.graphql";

/**
 * Imperative API exposed to the parent (User page), so the global Save
 * button on StepTabs can commit pending Module Access changes.
 */
export interface ModuleAccessSectionHandle {
  /** Commit pending grants/revokes. Shows toast(s) and refetches on completion. */
  save: () => Promise<void>;
  /** True when there are unsaved pending changes. */
  isDirty: () => boolean;
}

interface IModuleAccessSectionProps {
  /** Target user id. */
  userId: string;
}

interface ModuleRow {
  id: string;
  alias: string;
  name: string;
  /** Display label — comes from the launcher catalog when known, fallback to alias/name. */
  label: string;
  /** Accent color from the launcher catalog. */
  accent: string;
}

const FALLBACK_ACCENT = "#2968A8";

/**
 * Admin UI for managing UserModuleAccess on the user edit screen. Lists the
 * products the tenant has contracted (same source as the launcher,
 * `tenantProducts`), reusing the launcher presentation catalog (label +
 * accent) so the visual matches the product picker the end user sees.
 * TOPCON_IDENTITY is excluded — it is the platform itself, not a product, and
 * every user must pass through it to open any other module. Changes are staged
 * locally; the parent commits them through the imperative `save()` handle
 * (wired to the StepTabs global Save button). The MODULE_ACCESS_HAS_ROLES
 * error from the revoke mutation surfaces as a per-module entry in the error toast.
 */
const ModuleAccessSection = forwardRef<ModuleAccessSectionHandle, IModuleAccessSectionProps>(({ userId }, ref) => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [saving, setSaving] = useState(false);

  // Source of truth = the tenant's contracted products (the same query the
  // launcher uses), so this list only shows modules the user can actually
  // reach. TOPCON_IDENTITY is already excluded by the backend.
  const { products, loading: productsLoading } = useTenantProducts();

  const { data: userData, loading: userLoading, refetch: refetchUser } = useQuery<
    TUserByIdQueries,
    UserQueriesGetByIdArgs
  >(userById(["id", "admin", "userModuleAccesses.id", "userModuleAccesses.moduleId"]), {
    variables: { id: userId },
    fetchPolicy: "cache-and-network"
  });

  const [createMutation] = useMutation<
    TUserModuleAccessCreateMutations,
    UserModuleAccessMutationsCreateArgs
  >(userModuleAccessCreate());

  const [revokeMutation] = useMutation<
    TRevokeUserModuleAccessMutations,
    UserModuleAccessMutationsRevokeUserModuleAccessArgs
  >(revokeUserModuleAccess());

  const currentAccesses = userData?.user?.getByID?.result?.userModuleAccesses ?? [];
  // Admin users (typically @topconsuite staff) bypass UserModuleAccess: the
  // backend grants them every contracted tenant module implicitly. The section
  // becomes read-only and the checkboxes reflect the effective state.
  const isAdmin = Boolean(userData?.user?.getByID?.result?.admin);

  const modules = useMemo<ModuleRow[]>(() => {
    const rows: ModuleRow[] = [];

    products.forEach((p) => {
      if (!p || !p.moduleId || !p.module) return;
      // Only products the tenant has contracted are manageable here, so the
      // list mirrors what the user can actually open in the launcher.
      if (!p.contracted) return;
      // Defensive: the backend already drops TOPCON_IDENTITY (it is the
      // platform itself, not a product), but keep the intent explicit.
      if (p.module === "TOPCON_IDENTITY") return;

      const presentation = MODULE_PRESENTATION[p.module];

      rows.push({
        id: String(p.moduleId),
        alias: p.module,
        name: p.name,
        label: presentation?.label ?? p.module ?? p.name,
        accent: presentation?.accent ?? FALLBACK_ACCENT
      });
    });

    return rows.sort((a, b) => a.label.localeCompare(b.label));
  }, [products]);

  const accessByModuleId = useMemo(() => {
    const map = new Map<string, string>();

    currentAccesses.forEach((access) => {
      if (access?.id && access?.moduleId) {
        map.set(String(access.moduleId), String(access.id));
      }
    });

    return map;
  }, [currentAccesses]);

  // Pending diff layered on top of the saved state. Keys are moduleIds, value
  // is the *next* desired checked state. Modules whose desired state matches
  // the saved state are removed from the map (so isDirty stays accurate).
  const [pendingMap, setPendingMap] = useState<Map<string, boolean>>(new Map());

  const isChecked = useCallback(
    (moduleId: string) => {
      if (isAdmin) return true;
      if (pendingMap.has(moduleId)) return pendingMap.get(moduleId) === true;

      return accessByModuleId.has(moduleId);
    },
    [accessByModuleId, isAdmin, pendingMap]
  );

  const toggle = useCallback(
    (moduleId: string) => {
      if (isAdmin) return;
      const original = accessByModuleId.has(moduleId);
      const next = !isChecked(moduleId);

      setPendingMap((prev) => {
        const newMap = new Map(prev);

        if (next === original) {
          newMap.delete(moduleId);
        } else {
          newMap.set(moduleId, next);
        }

        return newMap;
      });
    },
    [accessByModuleId, isAdmin, isChecked]
  );

  const isDirty = useCallback(() => !isAdmin && pendingMap.size > 0, [isAdmin, pendingMap]);

  const save = useCallback(async () => {
    if (isAdmin) {
      addToast({ type: "info", title: t(UserRegisterMessages.moduleAccessAdminNotice) });

      return;
    }

    if (pendingMap.size === 0) {
      addToast({ type: "info", title: t(UserRegisterMessages.moduleAccessNoChanges) });

      return;
    }

    const toGrant: string[] = [];
    const toRevoke: string[] = [];

    pendingMap.forEach((nextChecked, moduleId) => {
      if (nextChecked) {
        toGrant.push(moduleId);
      } else {
        const accessId = accessByModuleId.get(moduleId);

        if (accessId) toRevoke.push(accessId);
      }
    });

    setSaving(true);
    const errors: string[] = [];

    // Sequential to surface per-module errors (e.g. MODULE_ACCESS_HAS_ROLES).
    for (const moduleId of toGrant) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await createMutation({
          variables: { userModuleAccess: { userId, moduleId } }
        });
        const result = data?.userModuleAccess?.create;

        if (result?.status !== ECommandResultStatus.SUCCESS) {
          errors.push(result?.message || "Grant failed");
        }
      } catch (e) {
        errors.push(e instanceof Error ? e.message : "Grant failed");
      }
    }

    for (const accessId of toRevoke) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await revokeMutation({ variables: { id: accessId } });
        const result = data?.userModuleAccess?.revokeUserModuleAccess;

        if (result?.status !== ECommandResultStatus.SUCCESS) {
          errors.push(result?.message || "Revoke failed");
        }
      } catch (e) {
        errors.push(e instanceof Error ? e.message : "Revoke failed");
      }
    }

    setPendingMap(new Map());
    await refetchUser();
    setSaving(false);

    if (errors.length > 0) {
      addToast({
        type: "error",
        title: t(UserRegisterMessages.moduleAccessSaveError),
        description: errors.join(" • ")
      });
    } else {
      addToast({ type: "success", title: t(UserRegisterMessages.moduleAccessSaveSuccess) });
    }
  }, [accessByModuleId, addToast, createMutation, isAdmin, pendingMap, refetchUser, revokeMutation, t, userId]);

  useImperativeHandle(ref, () => ({ save, isDirty }), [save, isDirty]);

  const loading = productsLoading || userLoading;

  return (
    <Stack spacing={1.5} sx={{ padding: "16px" }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {t(UserRegisterMessages.moduleAccessTitle)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {t(UserRegisterMessages.moduleAccessCaption)}
      </Typography>

      {!loading && isAdmin && (
        <Box
          sx={{
            padding: "12px 16px",
            borderRadius: "8px",
            border: "1px solid",
            borderColor: "warning.main",
            backgroundColor: "warning.light",
            color: "warning.contrastText"
          }}
        >
          <Typography variant="body2">{t(UserRegisterMessages.moduleAccessAdminNotice)}</Typography>
        </Box>
      )}

      {loading && <Typography variant="body2">{t(GlobalMessages.loading)}</Typography>}

      {!loading && modules.length === 0 && (
        <Typography variant="body2">{t(UserRegisterMessages.moduleAccessNoModules)}</Typography>
      )}

      {!loading && modules.map((mod) => {
        const checked = isChecked(mod.id);
        const disabled = saving || isAdmin;

        return (
          <Box
            key={mod.id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              padding: "12px 16px",
              borderRadius: "8px",
              border: "1px solid",
              borderColor: checked ? "primary.main" : "divider",
              backgroundColor: checked ? "action.hover" : "background.paper",
              cursor: disabled ? "not-allowed" : "pointer",
              transition: "border-color 120ms ease, background-color 120ms ease",
              opacity: disabled ? 0.6 : 1
            }}
            onClick={() => { if (!disabled) toggle(mod.id); }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (disabled) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggle(mod.id);
              }
            }}
          >
            <ModuleHex accent={mod.accent} size={32} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {mod.label}
              </Typography>
            </Box>
            <Checkbox
              checked={checked}
              disabled={disabled}
              onClick={(e) => e.stopPropagation()}
              onChange={() => toggle(mod.id)}
              inputProps={{ "aria-label": mod.label }}
            />
          </Box>
        );
      })}
    </Stack>
  );
});

ModuleAccessSection.displayName = "ModuleAccessSection";

export default ModuleAccessSection;
